// client/src/components/ticketing/PromoCodeManager.jsx
//
// CRUD panel for ticket promo codes on a ticketed event item
// (server/routes/ticketingRoutes.js — t_ticket_promo_code). Rendered as a
// tab inside TicketConfig.jsx, sharing its AuctionConfig.css theme.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const EMPTY_PROMO = {
  code: "",
  discount_type: "percent",
  discount_value: "10",
  max_uses: "",
  valid_until: "",
};

export default function PromoCodeManager({ tenantId, eventId, itemId }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;

  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY_PROMO);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const loadCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/promo-codes`);
      if (data.success) setCodes(data.promoCodes);
    } catch (err) {
      console.error("Error loading promo codes:", err);
    }
  }, [base]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const submitCode = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: "", type: "" });

    const payload = {
      code: form.code.trim(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      valid_until: form.valid_until || null,
    };

    try {
      const { data } = await axios.post(`${base}/promo-codes`, payload);
      if (data.success) {
        setMessage({ text: data.message, type: "success" });
        setForm(EMPTY_PROMO);
        loadCodes();
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to create promo code", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm("Delete this promo code?")) return;
    try {
      await axios.delete(`${base}/promo-codes/${codeId}`);
      loadCodes();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to delete promo code", type: "error" });
    }
  };

  return (
    <div>
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={submitCode} className="auction-config-form" style={{ padding: 16, borderRadius: 12, marginBottom: 16 }}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="promo_code">Code <span className="required">*</span></label>
            <input
              id="promo_code"
              className="uppercase"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder="EARLYBIRD10"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="promo_type">Discount Type</label>
            <select
              id="promo_type"
              value={form.discount_type}
              onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
            >
              <option value="percent">Percent off</option>
              <option value="flat">Flat amount off</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="promo_value">
              {form.discount_type === "percent" ? "Percent (0-100)" : "Amount off"} <span className="required">*</span>
            </label>
            <input
              id="promo_value"
              type="number"
              min="0"
              step="0.01"
              value={form.discount_value}
              onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="promo_max_uses">Max Uses</label>
            <input
              id="promo_max_uses"
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
              placeholder="Blank = unlimited"
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="promo_valid_until">Valid Until</label>
          <input
            id="promo_valid_until"
            type="datetime-local"
            value={form.valid_until}
            onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
          />
        </div>
        <div className="form-actions" style={{ marginTop: 12, paddingTop: 12 }}>
          <button type="submit" disabled={saving} className="save-button">
            {saving ? "Saving…" : "Add Promo Code"}
          </button>
        </div>
      </form>

      {codes.length === 0 ? (
        <div className="ticket-empty-state">No promo codes yet.</div>
      ) : (
        <ul className="ticket-list">
          {codes.map((code) => (
            <li key={code.id} className="ticket-list-item">
              <div>
                <div className="ticket-list-item-title">{code.code}</div>
                <div className="ticket-list-item-meta">
                  {code.discount_type === "percent"
                    ? `${Number(code.discount_value)}% off`
                    : `${Number(code.discount_value).toFixed(2)} off`}{" "}
                  · {code.uses_count}/{code.max_uses ?? "∞"} used
                </div>
              </div>
              <button onClick={() => deleteCode(code.id)} className="ticket-item-btn danger">
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
